import {
    formatStatCount,
    loadPublicStats,
    PUBLIC_STATS_SNAPSHOT,
    publicStatsEndpoint,
    type PublicStats,
} from 'pb-public-stats';

/**
 * The stats endpoint on this deploy's API. Every hive caller of
 * `loadPublicStats` derives the URL through here so a non-prod lane cannot end
 * up reading production counts on one surface and its own on another.
 *
 * The package resolves the path against the base's ORIGIN rather than
 * concatenating: hive's `apiDomain` already carries `/v3`, and joining the two
 * asked for `/v3/v3/public/companies/stats` — a 404 the loader absorbed into the
 * committed snapshot, so every count in the app aged in silence.
 */
export const hivePublicStatsEndpoint = (): string =>
    publicStatsEndpoint(useRuntimeConfig().public.pb.apiDomain);

/**
 * Coverage counts for the app shell and the plan copy, bound to Nuxt's shared state.
 *
 * The numbers themselves live in pb-public-stats — this is only the binding.
 * Seeded from the committed snapshot, which is compiled into the bundle at build
 * time, so `stats` is a `PublicStats` from the first tick and never null: a
 * member who loads the sidebar while the API is down reads last week's exact
 * count, not "0 Data Brokers". The live counts arrive onMounted.
 *
 * The loader's promise cache makes that ONE request however many components
 * call this, and they all read this one `useState` key — so the sidebar count,
 * the search-presence copy and the plan benefits move together and never
 * disagree.
 */
export const useBrokerStats = () => {
    const stats = useState<PublicStats>('public-stats', () => PUBLIC_STATS_SNAPSHOT.stats);
    const endpoint = hivePublicStatsEndpoint();

    onMounted(() => {
        loadPublicStats(endpoint).then((live) => {
            stats.value = live;
        });
    });

    return {
        stats,
        formattedBrokerCount: computed(() => formatStatCount(stats.value.data_brokers)),
        formattedCustomSiteCount: computed(() => formatStatCount(stats.value.custom_sites)),
    };
};
